// Phase RV-5 — useUpcomingBoard hook.
//
// Anon-readable Supabase reader against rollover_candidates, scoped to the
// active board window. Spec: ROLLOVER Intelligence Enhancement §11.1.
//
// Doctrine carry-forward:
//   - Anon JWT only; relies on the RV-1 RLS policy that grants anon SELECT
//     to rollover_candidates rows created within the last 30 days.
//   - Realtime channel name uses useId() (per-mount, never Math.random())
//     to match the JB-arc / usePendingDrift cleanup pattern.
//   - N16: when no tier has >=1 selected/evaluated row, the UI must show a
//     countdown, not a blank pool. The hook surfaces enough state for that
//     decision (firstKickoffAt + publishedAt) without fabricating.

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { getBrowserClient } from "../supabase/browser.ts";
import {
  type BoardCandidate,
  type WindowLabel,
  currentWindowPublicationTime,
  detectCurrentWindow,
  firstKickoffAt as computeFirstKickoff,
} from "./upcomingBoardHelpers.ts";
import type { RolloverTier } from "./tierConfig.ts";

export interface UseUpcomingBoardState {
  candidates: BoardCandidate[];
  windowLabel: WindowLabel | null;
  publishedAt: Date | null;
  firstKickoffAt: Date | null;
  isLive: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export interface UseUpcomingBoardOptions {
  /** Override the active window. Default: auto-detect from current time. */
  windowFilter?: WindowLabel | null;
  /** Override "now" — for tests. */
  now?: Date;
}

function asTier(v: unknown): RolloverTier | null {
  return v === "2x" || v === "3x" || v === "4x" || v === "5x" || v === "6x" ? v : null;
}
function asWindow(v: unknown): WindowLabel | null {
  return v === "midweek" || v === "weekend" || v === "supplement" ? v : null;
}
function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function asNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
function asPool(v: unknown): BoardCandidate["pool_tag"] | null {
  return v === "selected" || v === "evaluated" || v === "assessed" ? v : null;
}
function asFilterStage(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function normaliseRow(
  raw: Record<string, unknown>,
): BoardCandidate | null {
  const id = asStr(raw.candidate_id);
  const name = asStr(raw.match_name);
  const tier = asTier(raw.tier);
  const w = asWindow(raw.window_label);
  const pool = asPool(raw.pool_tag);
  if (!id || !name || !tier || !w || !pool) return null;
  return {
    candidate_id: id,
    fixture_id: asStr(raw.fixture_id),
    match_name: name,
    league: asStr(raw.league),
    kickoff_at: asStr(raw.kickoff_at),
    market: asStr(raw.market),
    pick: asStr(raw.pick),
    odds: asNum(raw.odds),
    confidence_score: asNum(raw.confidence_score),
    value_edge_pct: asNum(raw.value_edge_pct),
    clv_edge_detected: raw.clv_edge_detected === true,
    pool_tag: pool,
    discard_reason: asStr(raw.discard_reason),
    agent_reasoning: asStr(raw.agent_reasoning),
    filter_stage_failed: asFilterStage(raw.filter_stage_failed),
    tier,
    window_label: w,
  };
}

export function useUpcomingBoard(
  opts: UseUpcomingBoardOptions = {},
): UseUpcomingBoardState {
  const { windowFilter = null, now: nowOverride } = opts;
  const supabase = useMemo(() => getBrowserClient(), []);
  const instanceId = useId();

  const activeWindow: WindowLabel | null = useMemo(() => {
    if (windowFilter) return windowFilter;
    return detectCurrentWindow(nowOverride);
  }, [windowFilter, nowOverride]);

  const publishedAt: Date | null = useMemo(() => {
    if (!activeWindow) return null;
    return currentWindowPublicationTime(activeWindow, nowOverride);
  }, [activeWindow, nowOverride]);

  const [candidates, setCandidates] = useState<BoardCandidate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!activeWindow || !publishedAt) {
      setCandidates([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any;
      const res = await sb
        .from("rollover_candidates")
        .select(
          "candidate_id, fixture_id, match_name, league, kickoff_at, market, pick, odds, confidence_score, value_edge_pct, clv_edge_detected, pool_tag, discard_reason, agent_reasoning, filter_stage_failed, tier, window_label, created_at",
        )
        .eq("window_label", activeWindow)
        .gte("created_at", publishedAt.toISOString())
        .order("tier", { ascending: true })
        .order("confidence_score", { ascending: false });
      if (res.error) {
        setError(res.error.message ?? "Board query failed");
        setLoading(false);
        return;
      }
      const rows = Array.isArray(res.data) ? res.data : [];
      const normalised: BoardCandidate[] = [];
      for (const r of rows as Array<Record<string, unknown>>) {
        const norm = normaliseRow(r);
        if (norm) normalised.push(norm);
      }
      setCandidates(normalised);
      setLoading(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Board query failed");
      setLoading(false);
    }
  }, [supabase, activeWindow, publishedAt]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // Realtime — per-mount channel, anon-safe (read-only stream).
  useEffect(() => {
    if (!activeWindow) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const channel: RealtimeChannel = sb
      .channel(`rollover_candidates:${activeWindow}:${instanceId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "rollover_candidates",
          filter: `window_label=eq.${activeWindow}`,
        },
        () => {
          void refetch();
        },
      )
      .subscribe();
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any).removeChannel(channel);
    };
  }, [supabase, activeWindow, instanceId, refetch]);

  const firstKickoffAt: Date | null = useMemo(
    () => computeFirstKickoff(candidates),
    [candidates],
  );

  const isLive: boolean = useMemo(() => {
    if (!firstKickoffAt) return false;
    const ref = nowOverride ?? new Date();
    return ref.getTime() >= firstKickoffAt.getTime();
  }, [firstKickoffAt, nowOverride]);

  return useMemo<UseUpcomingBoardState>(
    () => ({
      candidates,
      windowLabel: activeWindow,
      publishedAt,
      firstKickoffAt,
      isLive,
      loading,
      error,
      refetch,
    }),
    [
      candidates,
      activeWindow,
      publishedAt,
      firstKickoffAt,
      isLive,
      loading,
      error,
      refetch,
    ],
  );
}
